import { useQueryClient } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getCurrentUser } from 'aws-amplify/auth';
import { events } from 'aws-amplify/data';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RealtimeEventEnvelope,
  RealtimeResourceWatch,
  RealtimeRuntimeConfig,
  getRealtimeGlobalNoticeMessage,
  realtimeChannelFor,
  realtimeResourceKey,
} from '../api/realtime';
import { getFeatureGates } from '../api/network_calls';
import { refresh_stories } from '../store/appSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';

type RealtimeCallbacks = {
  onEvent?: (event: RealtimeEventEnvelope) => void;
  onReconcile?: () => void;
  suppressGlobalNotice?: boolean;
};

type RegistryEntry = {
  watch: RealtimeResourceWatch;
  refCount: number;
  persistent: boolean;
  callbacks: Set<RealtimeCallbacks>;
  channel?: Awaited<ReturnType<typeof events.connect>>;
  subscription?: { unsubscribe: () => void };
  connecting?: Promise<void>;
  closeTimer?: ReturnType<typeof setTimeout>;
  reconnectTimer?: ReturnType<typeof setTimeout>;
  expiryTimer?: ReturnType<typeof setTimeout>;
  reconcileTimer?: ReturnType<typeof setTimeout>;
  reconnectAttempt: number;
};

interface RealtimeContextValue {
  enabled: boolean;
  isConnected: boolean;
  connectedResourceKeys: string[];
  enabledResourceTypes: RealtimeRuntimeConfig['enabledResourceTypes'];
  watchResource: (
    watch: RealtimeResourceWatch,
    callbacks?: RealtimeCallbacks,
  ) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);
const STORAGE_PREFIX = 'fable:realtime-active:v1';
const CLOSE_GRACE_MS = 60_000;
const MAX_PERSISTED_WATCHES = 100;
const MAX_ACTIVE_WATCHES = 180;

const closeInterruptedChannel = (
  channel: NonNullable<RegistryEntry['channel']>,
) => {
  try {
    Promise.resolve(channel.close()).catch(error => {
      console.warn('[Realtime] Failed to close interrupted channel:', error);
    });
  } catch (error) {
    console.warn('[Realtime] Failed to close interrupted channel:', error);
  }
};

const patchStoryQueryData = (
  current: any,
  storyId: string,
  patch: Record<string, any>,
): any => {
  if (!current) return current;
  if (Array.isArray(current)) {
    return current.map(story => story?.storyId === storyId ? { ...story, ...patch } : story);
  }
  if (Array.isArray(current.stories)) {
    return {
      ...current,
      stories: current.stories.map((story: any) =>
        story?.storyId === storyId ? { ...story, ...patch } : story
      ),
    };
  }
  return current.storyId === storyId ? { ...current, ...patch } : current;
};

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(state => state.app.userId);
  const [channelUserId, setChannelUserId] = useState<string | null>(null);
  const stories = useAppSelector(state => state.app.stories);
  const storiesRef = useRef(stories);
  storiesRef.current = stories;
  const [runtimeConfig, setRuntimeConfig] = useState<RealtimeRuntimeConfig | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedResourceKeys, setConnectedResourceKeys] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ message: string; status: 'completed' | 'failed' } | null>(null);
  const registryRef = useRef(new Map<string, RegistryEntry>());
  const seenEventIdsRef = useRef(new Set<string>());
  const latestRevisionRef = useRef(new Map<string, number>());
  const noticeOperationIdsRef = useRef(new Set<string>());
  const storageKey = userId ? `${STORAGE_PREFIX}:${userId}` : null;

  const updateConnectionState = useCallback(() => {
    const connectedKeys = [...registryRef.current.entries()]
      .filter(([, entry]) => Boolean(entry.channel))
      .map(([key]) => key)
      .sort();
    setConnectedResourceKeys(connectedKeys);
    setIsConnected(connectedKeys.length > 0);
  }, []);

  const persistRegistry = useCallback(() => {
    if (!storageKey) return;
    const now = Date.now();
    const watches = [...registryRef.current.values()]
      .filter(entry => entry.persistent && Date.parse(entry.watch.watchUntil) > now)
      .slice(0, MAX_PERSISTED_WATCHES)
      .map(entry => entry.watch);
    localStorage.setItem(storageKey, JSON.stringify(watches));
  }, [storageKey]);

  const closeEntry = useCallback(async (entry: RegistryEntry) => {
    if (entry.closeTimer) clearTimeout(entry.closeTimer);
    if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
    if (entry.expiryTimer) clearTimeout(entry.expiryTimer);
    if (entry.reconcileTimer) clearTimeout(entry.reconcileTimer);
    entry.closeTimer = undefined;
    entry.reconnectTimer = undefined;
    entry.expiryTimer = undefined;
    entry.reconcileTimer = undefined;
    entry.subscription?.unsubscribe();
    entry.subscription = undefined;
    const channel = entry.channel;
    entry.channel = undefined;
    entry.connecting = undefined;
    if (channel) {
      try {
        await channel.close();
      } catch {
        // Navigation teardown is best-effort.
      }
    }
    if (entry.refCount === 0 && !entry.persistent) {
      for (const [key, candidate] of registryRef.current.entries()) {
        if (candidate === entry) {
          registryRef.current.delete(key);
          break;
        }
      }
    }
    updateConnectionState();
  }, [updateConnectionState]);

  const armExpiry = useCallback((entry: RegistryEntry) => {
    if (entry.expiryTimer) clearTimeout(entry.expiryTimer);
    const remainingMs = Date.parse(entry.watch.watchUntil) - Date.now();
    if (remainingMs <= 0) {
      entry.persistent = false;
      persistRegistry();
      void closeEntry(entry);
      return;
    }
    entry.expiryTimer = setTimeout(() => {
      entry.expiryTimer = undefined;
      entry.persistent = false;
      persistRegistry();
      void closeEntry(entry);
    }, remainingMs);
  }, [closeEntry, persistRegistry]);

  const reconcileResource = useCallback((
    event: RealtimeEventEnvelope,
    fetchAuthoritativeState: boolean,
  ) => {
    const patch = event.payload || {};
    const shouldFetchAuthoritativeState =
      event.status !== 'running' && fetchAuthoritativeState;
    if (event.resourceType === 'story' && userId) {
      queryClient.setQueriesData(
        { queryKey: ['stories', userId] },
        (current: any) => patchStoryQueryData(current, event.resourceId, patch),
      );
      dispatch(refresh_stories({
        stories: storiesRef.current.map((story: any) =>
          story.storyId === event.resourceId ? { ...story, ...patch } : story
        ),
        last_refreshed_at: Date.now(),
        last_story_edited_at: Date.now(),
      }));
      if (shouldFetchAuthoritativeState) {
        queryClient.invalidateQueries({ queryKey: ['stories', userId] }, { cancelRefetch: false });
      }
    } else if (
      shouldFetchAuthoritativeState &&
      event.resourceType === 'character' &&
      userId
    ) {
      queryClient.invalidateQueries({ queryKey: ['characters', userId] }, { cancelRefetch: false });
    } else if (
      shouldFetchAuthoritativeState &&
      event.resourceType === 'voice' &&
      userId
    ) {
      queryClient.invalidateQueries({ queryKey: ['voices', userId] }, { cancelRefetch: false });
    } else if (event.resourceType === 'cover') {
      queryClient.setQueryData(['coverStatus', event.resourceId], (current: any) => ({
        ...(current || {}),
        jobId: event.resourceId,
        status: event.status === 'completed' ? 'completed' : event.status,
        ...patch,
      }));
      if (shouldFetchAuthoritativeState) {
        queryClient.invalidateQueries({ queryKey: ['userCovers', userId] }, { cancelRefetch: false });
      }
    } else if (event.resourceType === 'book-preview') {
      queryClient.setQueryData(['previewPdfStatus', event.resourceId], (current: any) => ({
        ...(current || {}),
        jobId: event.resourceId,
        status: event.status === 'completed' ? 'completed' : event.status,
        ...patch,
      }));
    }
  }, [dispatch, queryClient, userId]);

  const connectEntryRef = useRef<(entry: RegistryEntry) => Promise<void>>(async () => {});

  const handleEvent = useCallback((entry: RegistryEntry, event: RealtimeEventEnvelope) => {
    if (!event?.eventId || event.version !== 1 || seenEventIdsRef.current.has(event.eventId)) return;
    seenEventIdsRef.current.add(event.eventId);
    if (seenEventIdsRef.current.size > 1000) {
      const oldest = seenEventIdsRef.current.values().next().value;
      if (oldest) seenEventIdsRef.current.delete(oldest);
    }
    const key = realtimeResourceKey(event.resourceType, event.resourceId);
    const revisionKey = `${key}:${event.operationId}`;
    const latestRevision = latestRevisionRef.current.get(revisionKey) ?? -1;
    if (event.revision < latestRevision) return;
    latestRevisionRef.current.set(revisionKey, event.revision);
    if (latestRevisionRef.current.size > 2000) {
      const oldest = latestRevisionRef.current.keys().next().value;
      if (oldest) latestRevisionRef.current.delete(oldest);
    }

    const hasReconcileCallback = [...entry.callbacks].some(
      callbacks => Boolean(callbacks.onReconcile),
    );
    reconcileResource(event, !hasReconcileCallback);
    entry.callbacks.forEach(callbacks => callbacks.onEvent?.(event));
    if (event.status !== 'running') {
      if (entry.reconcileTimer) clearTimeout(entry.reconcileTimer);
      entry.reconcileTimer = setTimeout(() => {
        entry.reconcileTimer = undefined;
        entry.callbacks.forEach(callbacks => callbacks.onReconcile?.());
      }, 750);
    }

    const suppressGlobalNotice = [...entry.callbacks].some(
      callbacks => callbacks.suppressGlobalNotice === true,
    );
    const isDefaultGlobalNotice =
      event.resourceType !== 'story' ||
      event.type === 'story.complete' ||
      event.type === 'story.failed';
    if (
      event.status !== 'running' &&
      isDefaultGlobalNotice &&
      !suppressGlobalNotice &&
      !noticeOperationIdsRef.current.has(event.operationId)
    ) {
      noticeOperationIdsRef.current.add(event.operationId);
      if (noticeOperationIdsRef.current.size > 1000) {
        const oldest = noticeOperationIdsRef.current.values().next().value;
        if (oldest) noticeOperationIdsRef.current.delete(oldest);
      }
      setNotice({
        message: getRealtimeGlobalNoticeMessage(event),
        status: event.status,
      });
      window.setTimeout(() => setNotice(null), 5000);
    }
    if (event.status !== 'running') {
      const payload = event.payload || {};
      const hasPendingFollowUp =
        payload.pending === true ||
        payload.pendingVideoCreation === true ||
        payload.pendingFFmpeg === true ||
        payload.pendingLandscape === true ||
        payload.pendingSubtitles === true;
      const isPartialStoryTerminal =
        event.resourceType === 'story' &&
        (event.type.includes('.landscape.') || event.type.includes('.subtitles.'));
      entry.persistent =
        hasPendingFollowUp ||
        (isPartialStoryTerminal && entry.persistent);
      if (entry.persistent) {
        const safetyWatchUntil = new Date(Date.now() + 15 * 60 * 1000).toISOString();
        if (Date.parse(safetyWatchUntil) > Date.parse(entry.watch.watchUntil)) {
          entry.watch = { ...entry.watch, watchUntil: safetyWatchUntil };
        }
        armExpiry(entry);
      }
      persistRegistry();
      if (entry.refCount === 0 && !entry.persistent) {
        entry.closeTimer = setTimeout(() => void closeEntry(entry), CLOSE_GRACE_MS);
      }
    }
  }, [armExpiry, closeEntry, persistRegistry, reconcileResource]);

  const scheduleReconnect = useCallback((entry: RegistryEntry) => {
    if (entry.reconnectTimer || Date.parse(entry.watch.watchUntil) <= Date.now()) return;
    const base = Math.min(30_000, 1_000 * 2 ** entry.reconnectAttempt);
    const delay = Math.round(base * (0.5 + Math.random()));
    entry.reconnectAttempt += 1;
    entry.reconnectTimer = setTimeout(() => {
      entry.reconnectTimer = undefined;
      void connectEntryRef.current(entry);
    }, delay);
  }, []);

  const connectEntry = useCallback(async (entry: RegistryEntry): Promise<void> => {
    if (
      !channelUserId ||
      !runtimeConfig?.endpoint ||
      runtimeConfig.enabledResourceTypes[entry.watch.resourceType] !== true ||
      entry.channel ||
      entry.connecting ||
      Date.parse(entry.watch.watchUntil) <= Date.now()
    ) return;

    const connection = (async () => {
      try {
        const channel = await events.connect(
          realtimeChannelFor(channelUserId, entry.watch.resourceType, entry.watch.resourceId),
          { authMode: 'userPool' },
        );
        entry.channel = channel;
        entry.reconnectAttempt = 0;
        entry.subscription = channel.subscribe({
          next: data => {
            const rawEvent = (data as any)?.event ?? data;
            try {
              const event = typeof rawEvent === 'string'
                ? JSON.parse(rawEvent)
                : rawEvent;
              handleEvent(entry, event as RealtimeEventEnvelope);
            } catch (error) {
              console.warn('[Realtime] Ignoring malformed event:', error);
            }
          },
          error: error => {
            console.warn('[Realtime] Subscription interrupted:', error);
            entry.subscription = undefined;
            entry.channel = undefined;
            closeInterruptedChannel(channel);
            updateConnectionState();
            scheduleReconnect(entry);
          },
        });
        updateConnectionState();
        entry.callbacks.forEach(callbacks => callbacks.onReconcile?.());
      } catch (error) {
        console.warn('[Realtime] Unable to connect resource channel:', error);
        const interruptedChannel = entry.channel;
        entry.channel = undefined;
        if (interruptedChannel) {
          closeInterruptedChannel(interruptedChannel);
        }
        updateConnectionState();
        scheduleReconnect(entry);
      } finally {
        entry.connecting = undefined;
      }
    })();
    entry.connecting = connection;
    await connection;
  }, [channelUserId, handleEvent, runtimeConfig, scheduleReconnect, updateConnectionState]);
  connectEntryRef.current = connectEntry;

  const watchResource = useCallback((
    watch: RealtimeResourceWatch,
    callbacks: RealtimeCallbacks = {},
  ): (() => void) => {
    const key = realtimeResourceKey(watch.resourceType, watch.resourceId);
    let entry = registryRef.current.get(key);
    if (!entry) {
      if (registryRef.current.size >= MAX_ACTIVE_WATCHES) {
        console.warn(
          `[Realtime] Active watch limit (${MAX_ACTIVE_WATCHES}) reached; polling fallback will handle ${key}.`,
        );
        return () => {};
      }
      entry = {
        watch,
        refCount: 0,
        persistent: true,
        callbacks: new Set(),
        reconnectAttempt: 0,
      };
      registryRef.current.set(key, entry);
    } else if (Date.parse(watch.watchUntil) > Date.parse(entry.watch.watchUntil)) {
      entry.watch = watch;
    }
    if (entry.closeTimer) clearTimeout(entry.closeTimer);
    entry.closeTimer = undefined;
    entry.refCount += 1;
    entry.persistent = true;
    entry.callbacks.add(callbacks);
    armExpiry(entry);
    persistRegistry();
    void connectEntry(entry);

    let released = false;
    return () => {
      if (released || !entry) return;
      released = true;
      entry.callbacks.delete(callbacks);
      entry.refCount = Math.max(0, entry.refCount - 1);
      if (entry.refCount === 0 && !entry.persistent) {
        entry.closeTimer = setTimeout(() => void closeEntry(entry!), CLOSE_GRACE_MS);
      }
    };
  }, [armExpiry, closeEntry, connectEntry, persistRegistry]);

  useEffect(() => {
    if (!userId) {
      registryRef.current.forEach(entry => void closeEntry(entry));
      registryRef.current.clear();
      setChannelUserId(null);
      setRuntimeConfig(null);
      return;
    }
    let cancelled = false;
    const configureRealtime = async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) return;
        const currentUser = await getCurrentUser();
        const response = await getFeatureGates(userId);
        const config = response?.realtime as RealtimeRuntimeConfig | undefined;
        if (cancelled || !config?.endpoint) return;
        const currentConfig = Amplify.getConfig();
        Amplify.configure({
          ...currentConfig,
          API: {
            ...(currentConfig.API || {}),
            Events: {
              endpoint: config.endpoint,
              region: config.region || 'us-east-1',
              defaultAuthMode: 'userPool',
            },
          },
        } as any);
        setChannelUserId(currentUser.userId);
        setRuntimeConfig(config);
      } catch (error) {
        console.warn('[Realtime] Configuration unavailable; polling remains active:', error);
      }
    };
    void configureRealtime();
    return () => { cancelled = true; };
  }, [closeEntry, userId]);

  useEffect(() => {
    if (!storageKey || !runtimeConfig) return;
    try {
      const serialized = localStorage.getItem(storageKey);
      const watches = serialized ? JSON.parse(serialized) : [];
      if (!Array.isArray(watches)) return;
      const now = Date.now();
      watches.slice(0, MAX_PERSISTED_WATCHES).forEach((watch: RealtimeResourceWatch) => {
        if (
          !watch?.resourceId ||
          Date.parse(watch.watchUntil) <= now ||
          runtimeConfig.enabledResourceTypes[watch.resourceType] !== true
        ) return;
        const key = realtimeResourceKey(watch.resourceType, watch.resourceId);
        if (!registryRef.current.has(key)) {
          const entry: RegistryEntry = {
            watch,
            refCount: 0,
            persistent: true,
            callbacks: new Set(),
            reconnectAttempt: 0,
          };
          registryRef.current.set(key, entry);
          armExpiry(entry);
          void connectEntry(entry);
        }
      });
      persistRegistry();
    } catch (error) {
      console.warn('[Realtime] Failed to restore active operations:', error);
    }
  }, [armExpiry, connectEntry, persistRegistry, runtimeConfig, storageKey]);

  useEffect(() => {
    registryRef.current.forEach(entry => void connectEntry(entry));
  }, [connectEntry, runtimeConfig]);

  useEffect(() => {
    const reconcileVisibleResources = () => {
      if (document.visibilityState !== 'visible') return;
      registryRef.current.forEach(entry => {
        if (Date.parse(entry.watch.watchUntil) <= Date.now()) return;
        entry.callbacks.forEach(callbacks => callbacks.onReconcile?.());
        void connectEntry(entry);
      });
    };
    document.addEventListener('visibilitychange', reconcileVisibleResources);
    window.addEventListener('online', reconcileVisibleResources);
    return () => {
      document.removeEventListener('visibilitychange', reconcileVisibleResources);
      window.removeEventListener('online', reconcileVisibleResources);
    };
  }, [connectEntry]);

  useEffect(() => () => {
    registryRef.current.forEach(entry => void closeEntry(entry));
    registryRef.current.clear();
  }, [closeEntry]);

  const value = useMemo<RealtimeContextValue>(() => ({
    enabled: Boolean(runtimeConfig?.endpoint),
    isConnected,
    connectedResourceKeys,
    enabledResourceTypes: runtimeConfig?.enabledResourceTypes || {},
    watchResource,
  }), [connectedResourceKeys, isConnected, runtimeConfig, watchResource]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
      {notice && (
        <div
          role="status"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 18px',
            borderRadius: 10,
            color: '#fff',
            background: notice.status === 'completed' ? '#238636' : '#c62828',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            fontWeight: 600,
          }}
        >
          {notice.message}
        </div>
      )}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = (): RealtimeContextValue => {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used within RealtimeProvider');
  return context;
};
